import React from "react";

import { useTranslation } from "react-i18next";

import { BonusTypeEnum } from "@api-types/my-bonuses/Bonus";
import { BonusActivityInfo } from "@api-types/withdrawal/WithdrawalDataDto";
import { BonusCardShopName } from "@components-shared/BonusCardShopName";
import InfoMessageComponent from "@components-shared/InfoMessageComponent";
import classNames from "classnames";
import { DateTime } from "luxon";

export default function WithdrawalActiveBonus({
	status,
	infoMessage,
}: {
	status: BonusActivityInfo;
	infoMessage?: string;
}) {
	const { t } = useTranslation("my-bonuses");

	const iconClasses = classNames("u-icon u-icon--xxlrg", {
		"u-icon--deposit--alt": status.bonusType == BonusTypeEnum.Deposit,
		"u-icon--my-bonuses--alt":
			status.bonusType == BonusTypeEnum.Voucher ||
			status.bonusType == BonusTypeEnum.Coupon,
	});

	const bonusTitle = () => {
		switch (status.bonusType) {
			case BonusTypeEnum.Coupon:
				return "COUPON_BONUS.TITLE";
			case BonusTypeEnum.Voucher:
				return "VOUCHER_BONUS.TITLE";
			case BonusTypeEnum.Deposit:
				return "DEPOSIT_BONUS.TITLE";
			case BonusTypeEnum.Undefined:
				return "";
			default:
				return "";
		}
	};

	if (!status || !bonusTitle) {
		return null;
	}

	return (
		<>
			<InfoMessageComponent
				type="note"
				message={t<string>(infoMessage || `BONUS_ACTIVE`)}
				addClassName="u-mar--bottom--med"
			/>

			<div className="card--bonus">
				<div className="card--bonus__header">
					<i className={iconClasses} />
					<div className="card--bonus__title">
						<div className="card--bonus__type">
							<span>{t<string>(bonusTitle())}: </span>
							<strong>
								{status.rolloverInfo.bonusAmount.toFixed(2)}{" "}
								{status.rolloverInfo?.currencyDisplayCode}
							</strong>
						</div>
						<BonusCardShopName shopName={status.shopName} />
						<div className="card--bonus__date">
							<span>{t<string>(`BONUS_ACTIVATED`)}: </span>
							<span>
								{DateTime.fromISO(
									status.rolloverInfo.activationTime
								).toLocaleString(DateTime.DATE_SHORT)}
							</span>
						</div>
					</div>
				</div>
				<div className="card--bonus__body">
					<div className="row">
						<div className="col col-sml-4">
							<div className="u-color--text--secondary">
								{t<string>(`BONUS_REQ_ROLLOVER`)}:
							</div>
							<div>
								{status.rolloverInfo.requiredRollover.toFixed(
									2
								)}{" "}
								{status.rolloverInfo.currencyDisplayCode}
							</div>
						</div>
						<div className="col col-sml-4">
							<div className="u-color--text--secondary">
								{t<string>(`BONUS_CURR_ROLLOVER`)}:
							</div>
							<div className="u-type--wgt--medium u-color--sent--success">
								{status.rolloverInfo.currentRollover.toFixed(2)}{" "}
								{status.rolloverInfo.currencyDisplayCode} (
								{status.rolloverInfo.currentRolloverPercentage}{" "}
								%)
							</div>
						</div>
						<div className="col col-sml-4">
							<div className="u-color--text--secondary">
								{t<string>(`BONUS_REMAINING_ROLLOVER`)}:
							</div>
							<div className="u-type--wgt--medium u-color--sent--danger">
								{status.rolloverInfo.rolloverRemainder.toFixed(
									2
								)}{" "}
								{status.rolloverInfo.currencyDisplayCode} (
								{
									status.rolloverInfo
										.rolloverRemainderPercentage
								}{" "}
								%)
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	);
}
